import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ArticleCard from "@/components/ArticleCard";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/categories";
import { faqs } from "@/mocks/site";

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const [copied, setCopied] = useState(false);
  const article = articles.find((a) => a.id === id);
  const related = articles.filter((a) => a.cat === article?.cat && a.id !== article?.id).slice(0, 3);
  const cat = categories.find((c) => c.id === article?.cat);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="flex min-h-[70vh] flex-col items-center justify-center px-4 pt-16 text-center md:pt-[72px]">
          <p className="text-6xl">🍉</p>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">
            内容不存在或已更新
          </h1>
          <p className="mt-2 text-sm text-foreground-600">你访问的这篇内容可能已被整理到其他栏目。</p>
          <Link
            to="/library"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回资料库
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Header />
      <main className="pt-16 md:pt-[72px]">
        <section className="mx-auto max-w-4xl px-4 py-8 md:px-6 md:py-12">
          <nav className="text-xs text-foreground-400" aria-label="面包屑">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/" className="hover:text-primary-700">
                  首页
                </Link>
              </li>
              <li>/</li>
              <li>
                <Link to="/library" className="hover:text-primary-700">
                  资料库
                </Link>
              </li>
              <li>/</li>
              <li>
                <Link to={`/library?cat=${article.cat}`} className="text-primary-700">
                  {cat?.name}
                </Link>
              </li>
            </ol>
          </nav>

          <div className="mt-6 flex items-center gap-2">
            <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-900">
              {article.tag}
            </span>
            <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-800">
              {article.catName}
            </span>
          </div>
          <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl md:leading-tight">
            {article.title}
          </h1>
          <p className="mt-4 border-l-2 border-accent-400 pl-4 text-sm leading-7 text-foreground-600 md:text-base">
            {article.excerpt}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-background-200 pb-6 text-xs text-foreground-400">
            <span className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 font-heading text-sm font-bold text-primary-700">
                瓜
              </span>
              <span className="font-medium text-foreground-700">{article.author}</span>
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {article.readTime} 分钟读完
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {article.hot} 热度
            </span>
          </div>
        </section>

        {/* content + aside */}
        <section className="mx-auto max-w-7xl px-4 pb-12 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_320px]">
            <article>
              <div className="overflow-hidden rounded-2xl">
                <img
                  src={article.cover}
                  alt={`${article.title} 头图`}
                  title={`${article.title} - 51瓜网`}
                  className="aspect-[16/9] w-full object-cover"
                />
              </div>

              <div className="mt-8 max-w-3xl">
                {article.blocks.map((b, i) => (
                  <div key={i} className="mb-7">
                    <h2 className="flex items-center gap-3 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-sm font-black text-primary-700">
                        {i + 1}
                      </span>
                      {b.h}
                    </h2>
                    {b.p.map((para, j) => (
                      <p key={j} className="mt-3 text-[15px] leading-8 text-foreground-700">
                        {para}
                      </p>
                    ))}
                  </div>
                ))}

                <div className="mt-8 rounded-2xl bg-accent-50 p-6 text-sm leading-7 text-foreground-700">
                  <p className="font-heading text-base font-bold text-foreground-900">写在最后</p>
                  <p className="mt-2">
                    以上内容为 51瓜网 对当下影视、综艺与娱乐动态的盘点整理，供你快速了解热点脉络。欢迎回到
                    51瓜网 资料库，查看更多分栏内容。
                  </p>
                </div>

                <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-5">
                  <button
                    onClick={copy}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-background-100 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:bg-accent-100 hover:text-accent-900"
                  >
                    {copied ? (
                      <>
                        <i className="ri-check-line text-secondary-700"></i>
                        已复制链接
                      </>
                    ) : (
                      <>
                        <i className="ri-link-m"></i>
                        复制链接
                      </>
                    )}
                  </button>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-foreground-500">分享到</span>
                    {["ri-qq-line", "ri-wechat-line", "ri-article-line"].map((c) => (
                      <a
                        key={c}
                        href="#"
                        onClick={(e) => e.preventDefault()}
                        className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700"
                        aria-label="分享"
                      >
                        <i className={`${c} text-base`}></i>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </article>

            <aside className="space-y-6">
              <div className="rounded-2xl border border-background-200 bg-background-100 p-6">
                <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-layout-3-line text-primary-600"></i>
                  其他相关栏目
                </h3>
                <div className="mt-4 space-y-2">
                  {categories.map((c) => (
                    <Link
                      key={c.id}
                      to={`/library?cat=${c.id}`}
                      className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors hover:bg-background-50 ${
                        c.id === article.cat ? "bg-background-50 font-semibold text-primary-700" : "text-foreground-600"
                      }`}
                    >
                      <i className={`${c.icon} ${c.id === article.cat ? "text-primary-600" : "text-foreground-300"}`}></i>
                      {c.name}
                    </Link>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl border border-background-200 bg-background-100 p-6">
                <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-questionnaire-line text-accent-500"></i>
                  你可能想问
                </h3>
                <div className="mt-4 space-y-3">
                  {faqs.slice(0, 3).map((f) => (
                    <Link
                      key={f.q}
                      to="/faq"
                      className="block rounded-lg bg-background-50 p-3 text-sm leading-6 text-foreground-700 transition-colors hover:text-primary-700"
                    >
                      {f.q}
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </section>

        {/* related */}
        {related.length > 0 && (
          <section className="bg-background-100">
            <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
              <div className="flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-xl font-extrabold text-foreground-950 md:text-2xl">
                  <i className="ri-arrow-right-up-line text-primary-600"></i>
                  相关推荐 · 更多{article.catName}
                </h2>
                <Link
                  to={`/library?cat=${article.cat}`}
                  className="flex items-center gap-1 text-sm font-medium text-primary-700 hover:text-primary-800"
                >
                  查看全部 <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-3">
                {related.map((a) => (
                  <ArticleCard key={a.id} a={a} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </div>
  );
}